import * as React from 'react';
import {
    bidderPreferenceLoadingSelector,
    bidderPreferenceSelector,
} from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import {
    fetchBidderPreferencesIfNeeded,
    updateBidderPreferences,
} from '@/redux/modules/bidder/preferences/bidderPreferences.actions';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { setPreferredCurrencyCookie } from '@/redux/modules/cookies';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import Radios from '@liveauctioneers/caterwaul-components/lib/Radio/Radios';
import styled from 'styled-components';

type Props = {
    className?: string;
    onSaveCallback?: () => void;
};

const CurrencySelection = ({ className, onSaveCallback }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const bidderPreferences = useAppSelector(bidderPreferenceSelector);
    const bidderPreferenceLoading = useAppSelector(bidderPreferenceLoadingSelector);
    const [selectedCurrency, setSelectedCurrency] = React.useState(bidderPreferences.preferredCurrency);

    const onChange = React.useCallback((event) => {
        setSelectedCurrency(event.currentTarget.name);
    }, []);

    const onSave = React.useCallback(() => {
        dispatch(
            updateBidderPreferences({
                ...bidderPreferences,
                preferredCurrency: selectedCurrency,
            })
        );
        onSaveCallback?.();
        dispatch(setPreferredCurrencyCookie());
    }, [dispatch, bidderPreferences, onSaveCallback, selectedCurrency]);

    React.useEffect(() => {
        setSelectedCurrency(bidderPreferences.preferredCurrency);
    }, [bidderPreferences]);

    React.useEffect(() => {
        dispatch(fetchBidderPreferencesIfNeeded({}));
    });

    return (
        <StyledCurrencySelection className={className}>
            <Radios
                onChange={onChange}
                selectedName={selectedCurrency}
            >
                <StyledRadio
                    label="GBP"
                    name="GBP"
                    testid="GBP"
                />
                <StyledRadio
                    label="EURO"
                    name="EUR"
                    testid="EUR"
                />
                <StyledRadio
                    label="USD"
                    name="USD"
                    testid="USD"
                />
                <StyledRadio
                    label={<FormattedMessage id="preferredCurrencyConversion.noConversion" />}
                    name="N/A"
                />
            </Radios>
            <StyledSaveButton
                data-testid="savePreferancesButton"
                loading={bidderPreferenceLoading}
                onClick={onSave}
            >
                {formatMessage({ id: 'preferredCurrencyConversion.save' })}
            </StyledSaveButton>
        </StyledCurrencySelection>
    );
};

export default CurrencySelection;

const StyledCurrencySelection = styled.div`
    max-width: 250px;
`;

const StyledRadio = styled(Radio)`
    margin-top: 10px;
    padding-bottom: 10px;
`;

const StyledSaveButton = styled(Button)`
    margin-top: 20px;
    width: 194px;
`;
